import { Combobox } from "@headlessui/react";
import { DocumentTextIcon, ChevronUpDownIcon, KeyIcon, PencilIcon, EyeIcon, MagnifyingGlassIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import { useLanguage } from "contexts/LanguageContext";
import { useStorage } from "hooks/useStorage";
import { useTemplates } from "hooks/useTemplates";
import { useQuotations } from "hooks/useQuotations";
import { useCustomers } from "hooks/useCustomers";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Customer } from "types/customer";
import { Quotation } from "types/quotation";
import { Template } from "types/template";
import { DocGenerator } from "utils/docGenerator";
import { extractTemplateFields } from "utils/extractDocFields";
import { isQuotation } from "./DocumentGeneratorModal";
import ChooseTemplatePage from "./steps/ChooseTemplatePage";
import EditDataPage from "./steps/EditDataPage";
import GeneratePage from "./steps/GeneratePage";
import { customerToKeyValuePairs } from "utils/customerDataMapper";
import { quotationToKeyValuePairs } from "utils/quotationDataMapper";
import ChooseTypePage from "./steps/ChooseTypePage";
import SelectPage from "./steps/SelectPage";
import QuotationSelectPage from "./steps/QuotationSelectPage";

interface RenderGlobalGenerationStepsProps {
  documentData: Record<string, string>;
  setDocumentData: (data: Record<string, string>) => void;
  isOpen: boolean;
  onClose: () => void;
  handleGenerateDocument: () => Promise<{url: string, fileName: string} | null | undefined>;
  selectedTemplate: Template | null;
  setSelectedTemplate: (template: Template | null) => void;
  selectedFormats: string[];
  setSelectedFormats: (formats: string[]) => void;
  isGenerating: boolean;
  setDocumentDataProviderCopy?: (documentDataProviderCopy: Quotation | Customer | null) => void;
  generatedFiles?: {url: string, fileName: string}[];
}

const RenderGlobalGenerationSteps = ({
  documentData = {},
  setDocumentData = () => { },
  isOpen = false,
  onClose = () => { },
  handleGenerateDocument = async () => { return null; },
  selectedTemplate = null,
  setSelectedTemplate = () => { },
  selectedFormats = ['docx'],
  setSelectedFormats = () => { },
  isGenerating = false,
  setDocumentDataProviderCopy = () => { },
  generatedFiles = [],
}: RenderGlobalGenerationStepsProps) => {
  const [templateFields, setTemplateFields] = useState<string[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingValue, setEditingValue] = useState<string>('');
  const [step, setStep] = useState<'type' | 'select' | 'quotation_select' | 'template' | 'edit' | 'generate'>('type');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [documentType, setDocumentType] = useState<'quotation' | 'contract' | null>(null);
  const [selectedItem, setSelectedItem] = useState<Quotation | Customer | null>(null);
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [generatedFileUrl, setGeneratedFileUrl] = useState<string | null>(null);
  const [generatedFileName, setGeneratedFileName] = useState<string | null>(null);

  const { uploadFile } = useStorage();
  const { t } = useLanguage();
  const { quotations, loading: quotationsLoading } = useQuotations();
  const { customers, loading: customersLoading } = useCustomers();

  const { loading, error, refreshTemplates, getTemplateByType } = useTemplates();
  const [templates, setTemplates] = useState<Template[]>([]);

  // Szűrt lista a keresés alapján
  const filteredItems = useMemo(() => {
    if (documentType === 'quotation') {
      return quotations.filter(quotation =>
        quotation.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        quotation.customerId.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else {
      return customers.filter(customer =>
        (customer.type === 'organization' && customer.companyName?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (customer.type === 'individual' && `${customer.firstName || ''} ${customer.lastName || ''}`.trim().toLowerCase().includes(searchQuery.toLowerCase())) ||
        customer.taxNumber?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
  }, [documentType, quotations, customers, searchQuery]);

  // Mező-érték párok rendezett listája és számláló a kitöltött értékekről
  const { sortedDocumentData, filledCount, filledPercentage } = useMemo(() => {
    if (!selectedItem) return { sortedDocumentData: [], filledCount: 0, filledPercentage: 0 };

    const filled = templateFields.filter(key => {
      const value = documentData[key];
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return value !== null && value !== undefined;
    }).length;

    const percentage = templateFields.length > 0 ? Math.round((filled / templateFields.length) * 100) : 0;

    return {
      sortedDocumentData: templateFields.map(key => [key, documentData[key] || '']),
      filledCount: filled,
      filledPercentage: percentage
    };
  }, [documentData, templateFields]);

  useEffect(() => {
    if (isOpen) {
      refreshTemplates();
    }
  }, [isOpen, refreshTemplates]);

  useEffect(() => {
    if (!isOpen) {
      // Reset all states when modal is closed
      setTemplateFields([]);
      setEditingKey('');
      setEditingValue('');
      setStep('type');
      setShowPreview(false);
      setPreviewUrl(null);
      setTemplateSearchQuery('');
      setSelectedTemplate(null);
      setGeneratedFileUrl(null);
      setGeneratedFileName(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!selectedItem) return;
      const templates = await getTemplateByType(isQuotation(selectedItem) ? 'quotation' : 'contract');
      setTemplates(templates);
    }
    fetchTemplates();
  }, [selectedItem, getTemplateByType]);

  useEffect(() => {
    if (selectedItem && setDocumentDataProviderCopy) {
      setDocumentDataProviderCopy(selectedItem);
    }
  }, [selectedItem, setDocumentDataProviderCopy]);

  const handleGenerateAndClose = async () => {
    if (handleGenerateDocument) {
      const result = await handleGenerateDocument();
      
      if (result) {
        setGeneratedFileUrl(result.url);
        setGeneratedFileName(result.fileName);
      }
    }
  };

  const handleTemplateSelect = async (template: Template) => {
    setSelectedTemplate(template);
    // Ha kiválasztottunk egy sablont, elemezzük a tartalmát
    if (template && template.fileUrl) {
      try {
        const fields = await extractTemplateFields(template);
        setTemplateFields(fields);

        // Mezők automatikus kitöltése a kiválasztott elem alapján
        if (selectedItem) {
          let newDocumentData: Record<string, string>;
          
          if (isQuotation(selectedItem)) {
            // Árajánlat adatok kinyerése
            newDocumentData = quotationToKeyValuePairs(selectedItem as Quotation);
            
            // Biztosítsuk, hogy a fizetési határidő mindig szerepeljen
            if (fields.includes('Fizetési határidő') && selectedItem.paymentDue) {
              newDocumentData['Fizetési határidő'] = `${selectedItem.paymentDue}`;
            }
            
            // Ha az árajánlat nem tartalmaz elég adatot az ügyfélről, akkor töltse ki az ügyféladatokból
            if (!selectedItem.companyName && selectedItem.customerId) {
              const customer = customers.find(c => c.id === selectedItem.customerId);
              if (customer) {
                // Kiegészítjük a hiányzó adatokat
                if (!newDocumentData['Cég megnevezése'] || newDocumentData['Cég megnevezése'].trim() === '') {
                  newDocumentData['Cég megnevezése'] = customer.type === 'organization' ? 
                    customer.companyName : 
                    `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
                }
                
                if (!newDocumentData['Cég címe'] || newDocumentData['Cég címe'].trim() === '') {
                  newDocumentData['Cég címe'] = customer.address ? 
                    [
                      customer.address.postalCode,
                      customer.address.city,
                      customer.address.street
                    ].filter(Boolean).join(', ') : '';
                }
                
                if (!newDocumentData['Cég adószáma'] || newDocumentData['Cég adószáma'].trim() === '') {
                  newDocumentData['Cég adószáma'] = customer.taxNumber || '';
                }
              }
            }
          } else {
            // Ügyfél adatokat közvetlen használjuk
            newDocumentData = customerToKeyValuePairs(selectedItem as Customer);
          }

          // Ha szerződéshez van kiválasztott árajánlat, azokat az adatokat is beillesztjük
          if (documentType === 'contract' && selectedQuotation) {
            const quotationData = quotationToKeyValuePairs(selectedQuotation);
            // Egyesítjük az adatokat, a quotation adatok felülírják az ügyfél adatokat
            Object.assign(newDocumentData, quotationData);
          }

          setDocumentData(newDocumentData);
        }
      } catch (error) {
        console.error('Hiba a sablon mezőinek kinyerése során:', error);
        toast.error('Nem sikerült kinyerni a sablon mezőit');
      }
    }
  };

  // Érték szerkesztés megkezdése
  const handleEdit = (key: string, value: string) => {
    setEditingKey(key);
    setEditingValue(value);
  };

  // Érték mentése
  const handleSaveEdit = () => {
    if (editingKey) {
      setDocumentData({
        ...documentData,
        [editingKey]: editingValue
      });
      setEditingKey('');
      setEditingValue('');
    }
  };

  // Érték szerkesztés megszakítása
  const handleCancelEdit = () => {
    setEditingKey('');
    setEditingValue('');
  };

  // Következő lépés
  const handleNextStep = () => {
    if (step === 'type') {
      console.log('Dokumentum típus kiválasztva:', documentType);
      setStep('select');
    } else if (step === 'select') {
      if (selectedItem) {
        console.log('Kiválasztott elem:', selectedItem);
        
        // Ha szerződést generálunk és ügyfelet választottunk ki, akkor árajánlat választó lépés következik
        if (documentType === 'contract' && !isQuotation(selectedItem)) {
          console.log('Szerződés generálás ügyféllel - árajánlat kiválasztása következik');
          setStep('quotation_select');
          return;
        }
        
        // Egyébként (árajánlat generálás vagy ha nincs ügyfél) a sablon választás következik
        const fetchTemplates = async () => {
          const templates = await getTemplateByType(documentType === 'quotation' ? 'quotation' : 'contract');
          console.log('Betöltött sablonok:', templates);
          setTemplates(templates);
        };
        fetchTemplates();
        setStep('template');
      }
    } else if (step === 'quotation_select') {
      // Árajánlat választás után a sablon választás következik
      const fetchTemplates = async () => {
        const templates = await getTemplateByType('contract'); // Mindig szerződés sablonok
        console.log('Betöltött sablonok szerződéshez:', templates);
        setTemplates(templates);
      };
      fetchTemplates();
      setStep('template');
    } else if (step === 'template') {
      setStep('edit');
    } else if (step === 'edit') {
      setStep('generate');
    } else if (step === 'generate') {
      setStep('edit');
    }
  };

  // "Átugrás" funkció az árajánlat kiválasztási lépéshez
  const handleSkipQuotationSelect = () => {
    setSelectedQuotation(null);
    handleNextStep();
  };

  // Előző lépés
  const handlePrevStep = () => {
    if (step === 'select') {
      setStep('type');
    } else if (step === 'quotation_select') {
      setStep('select');
    } else if (step === 'template') {
      // Ha az előző lépés az árajánlat kiválasztás volt, akkor oda menjünk vissza
      if (documentType === 'contract' && selectedItem && !isQuotation(selectedItem)) {
        setStep('quotation_select');
      } else {
        setStep('select');
      }
    } else if (step === 'edit') {
      setStep('template');
    } else if (step === 'generate') {
      setStep('edit');
    }
  };

  // Előnézet bezárása
  const closePreview = () => {
    setShowPreview(false);
    setPreviewUrl(null);
  };

  // Előnézet generálása a behelyettesített értékekkel
  const generatePreview = async () => {
    if (!selectedTemplate || !selectedTemplate.fileUrl) return;

    setIsGeneratingPreview(true);
    setIsLoadingPreview(true);

    try {
      // Generálunk egy dokumentumot a behelyettesített értékekkel
      const dataToUse = [documentData];

      const generatedDocs = await DocGenerator.generateDocuments(
        selectedTemplate,
        dataToUse,
        ['docx']
      );

      if (generatedDocs['docx'] && generatedDocs['docx'].length > 0) {
        const generatedDocumentBlob = generatedDocs['docx'][0];
        // Ideiglenes fájlnév generálása
        const tempFileName = `preview_${Date.now()}.docx`;

        // Feltöltjük a generált dokumentumot
        const fileUrl = await uploadFile(
          new File([generatedDocumentBlob], tempFileName, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }),
          'previews'
        );

        if (fileUrl) {
          // Közvetlenül a Firebase Storage URL-t használjuk
          setPreviewUrl(fileUrl);
          setShowPreview(true);
        } else {
          throw new Error("Nem sikerült feltölteni az előnézeti dokumentumot");
        }
      } else {
        throw new Error("Nem sikerült generálni az előnézeti dokumentumot");
      }
    } catch (error) {
      console.error("Hiba az előnézet generálása közben:", error);
      toast.error("Nem sikerült létrehozni az előnézetet");
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  return (
    <>
      {(step === 'type') && (
        <ChooseTypePage
          setDocumentType={setDocumentType}
          handleNextStep={handleNextStep}
        />
      )}
      {(step === 'select') && (
        <SelectPage
          documentType={documentType}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredItems={filteredItems}
          setSelectedItem={setSelectedItem}
          handleNextStep={handleNextStep}
          quotationsLoading={quotationsLoading}
          customersLoading={customersLoading}
        />
      )}
      {(step === 'quotation_select' && selectedItem && !isQuotation(selectedItem)) && (
        <QuotationSelectPage
          selectedCustomer={selectedItem as Customer}
          setSelectedQuotation={setSelectedQuotation}
          handleNextStep={handleNextStep}
          handleSkipStep={handleSkipQuotationSelect}
        />
      )}
      {(step === 'template') && (
        <ChooseTemplatePage
          documentDataProvider={selectedItem}
          templates={templates}
          selectedTemplate={selectedTemplate}
          loading={loading}
          handleTemplateSelect={handleTemplateSelect}
          templateSearchQuery={templateSearchQuery}
          setTemplateSearchQuery={setTemplateSearchQuery}
          templateFields={templateFields}
          isLoadingPreview={isLoadingPreview}
          setIsLoadingPreview={setIsLoadingPreview}
        />
      )}
      {(step === 'edit') && (
        <EditDataPage
          documentDataProvider={selectedItem}
          sortedDocumentData={sortedDocumentData}
          filledPercentage={filledPercentage}
          filledCount={filledCount}
          templateFields={templateFields}
          documentData={documentData}
          editingKey={editingKey}
          editingValue={editingValue}
          setEditingValue={setEditingValue}
          handleSaveEdit={handleSaveEdit}
          handleCancelEdit={handleCancelEdit}
          handleEdit={handleEdit}
          selectedTemplate={selectedTemplate}
          closePreview={closePreview}
        />
      )}
      {(step === 'generate') && (
        <GeneratePage
          selectedTemplate={selectedTemplate}
          selectedFormats={selectedFormats}
          setSelectedFormats={setSelectedFormats}
          isGenerating={isGenerating}
          handleGenerateAndClose={handleGenerateAndClose}
          sortedDocumentData={sortedDocumentData}
          filledPercentage={filledPercentage}
          filledCount={filledCount}
          generatedFileUrl={generatedFileUrl}
          generatedFileName={generatedFileName}
          generatedFiles={generatedFiles}
        />
      )}

      {/* Navigációs gombok - most fixen az oldal alján */}
      <div className="p-4 border-t border-gray-100 bg-white shadow-sm">
        <div className="flex justify-between items-center w-full mb-6">
          <div className="flex items-center">
            <button
              onClick={handlePrevStep}
              className="mr-4 bg-gray-50 hover:bg-gray-100 text-gray-700 py-1.5 px-3 rounded-md text-sm"
            >
              {t('common.back')}
            </button>
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${step === 'type' ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
              <div className={`w-3 h-3 rounded-full ${step === 'select' ? 'bg-cyan-500' : 'bg-gray-200'}`}></div>
              {/* Quotation selector step indiator - csak szerződés esetén mutatjuk */}
              {documentType === 'contract' && (
                <div className={`w-3 h-3 rounded-full ${step === 'quotation_select' ? 'bg-purple-500' : 'bg-gray-200'}`}></div>
              )}
              <div className={`w-3 h-3 rounded-full ${step === 'template' ? 'bg-emerald-500' : 'bg-gray-200'}`}></div>
              <div className={`w-3 h-3 rounded-full ${step === 'edit' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              <div className={`w-3 h-3 rounded-full ${step === 'generate' ? 'bg-amber-500' : 'bg-gray-200'}`}></div>
            </div>
          </div>
          {step !== 'generate' && (
            <button
              onClick={handleNextStep}
              disabled={
                (step === 'type' && !documentType) ||
                (step === 'select' && !selectedItem) ||
                (step === 'template' && !selectedTemplate)
              }
              className={`bg-teal-600 hover:bg-teal-700 text-white py-1.5 px-3 rounded-md text-sm ${
                (step === 'type' && !documentType) ||
                (step === 'select' && !selectedItem) ||
                (step === 'template' && !selectedTemplate)
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              {t('common.next')}
            </button>
          )}
        </div>
      </div>
    </>
  )
};

export default RenderGlobalGenerationSteps;

